import type { Npc } from './npc/types';

/** 10 player colors (spec §9 lines 614-629). */
export type ColorId =
  | 'red'
  | 'blue'
  | 'green'
  | 'yellow'
  | 'orange'
  | 'purple'
  | 'pink'
  | 'cyan'
  | 'white'
  | 'black';

/** 8 head ids (spec §9 lines 645-654). */
export type HeadId =
  | 'horse'
  | 'chicken'
  | 'cardboard-box'
  | 'shark'
  | 'pumpkin'
  | 'alien'
  | 'unicorn'
  | 'traffic-cone';

/** 8 eye states (spec §9 lines 656-665). */
export type EyeId =
  | 'dots'
  | 'angry'
  | 'sleepy'
  | 'crazy'
  | 'hearts'
  | 'x-eyes'
  | 'cyclops'
  | 'stars';

/** 9 cape ids (spec §9 lines 667-678). 'none' is the no-cape default. */
export type CapeId =
  | 'none'
  | 'starter'
  | 'fire'
  | 'rainbow'
  | 'cosmic'
  | 'golden'
  | 'wireframe'
  | 'water'
  | 'lava';

export interface CharacterCustomization {
  color: ColorId;
  head: HeadId;
  eyes: EyeId;
  cape: CapeId;
}

/** All 10 color ids in order — used by server for unique-color assignment. */
export const ALL_COLOR_IDS: readonly ColorId[] = [
  'red',
  'blue',
  'green',
  'yellow',
  'orange',
  'purple',
  'pink',
  'cyan',
  'white',
  'black',
] as const;

/** All selectable head ids in character-select order. */
export const ALL_HEAD_IDS: readonly HeadId[] = [
  'horse',
  'chicken',
  'cardboard-box',
  'shark',
  'pumpkin',
  'alien',
  'unicorn',
  'traffic-cone',
] as const;

/** All selectable eye ids in character-select order. */
export const ALL_EYE_IDS: readonly EyeId[] = [
  'dots',
  'angry',
  'sleepy',
  'crazy',
  'hearts',
  'x-eyes',
  'cyclops',
  'stars',
] as const;

/** All selectable cape ids in character-select order. */
export const ALL_CAPE_IDS: readonly CapeId[] = [
  'none',
  'starter',
  'fire',
  'rainbow',
  'cosmic',
  'golden',
  'wireframe',
  'water',
  'lava',
] as const;

/** Hex value for each ColorId. Single source of truth (matches docs/design/tokens.json). */
export const COLOR_ID_TO_HEX: Record<ColorId, number> = {
  red: 0xe04040,
  blue: 0x4080d0,
  green: 0x40b040,
  yellow: 0xe8d020,
  orange: 0xe08820,
  purple: 0x9040c0,
  pink: 0xe06090,
  cyan: 0x40b0c0,
  white: 0xe8e8e8,
  black: 0x404040,
};

/** Default non-color customization fields seeded at player creation. */
export const DEFAULT_CHARACTER_CUSTOMIZATION: Omit<CharacterCustomization, 'color'> = {
  head: 'horse',
  eyes: 'dots',
  cape: 'none',
};

/**
 * Turn state machine phases.
 * Spec §3.2: lobby → game_start → item_phase → rolling → moving → fork_choice
 *   → landing_effect → chest_phase → landing_effect → npc_phase → minigame_announce → minigame_countdown
 *   → minigame_play → minigame_results → awards → game_over
 *
 * Plan 3a implements: item_phase, rolling, moving, fork_choice, landing_effect.
 * Remaining phases (npc_phase, minigame_*, awards, game_over) are stubs for Plan 3b+.
 */
export type Phase =
  | 'lobby'
  | 'game_start'
  | 'item_phase'
  | 'rolling'
  | 'moving'
  | 'fork_choice'
  | 'landing_effect'
  | 'chest_phase'
  | 'npc_phase'
  | 'minigame_announce'
  | 'minigame_countdown'
  | 'minigame_play'
  | 'minigame_results'
  | 'awards'
  | 'game_over';

/** Board modifier flags in GameSettings. */
export interface BoardModifiers {
  pinata: boolean; // ALL keys drop on damage (instead of proportional)
  doubleTime: boolean; // 2x speed modifier
  oneHitKill: boolean; // All damage = instant kill
}

/** Per-player stats tracked for Pummel Awards. */
export interface PlayerStats {
  keysGained: number;
  keysLost: number;
  minigamesWon: number;
  minigamesLost: number;
  damageDealt: number;
  damageReceived: number;
  tilesTraveled?: number;
  itemsUsed?: number;
}

/**
 * Active Toxic Cloud state — lives on GameState, not Player. A cloud covers tileIds and deals
 * damagePerTick damage to any player whose nodeId is in tileIds at the start of their turn, for
 * each turn until round `expiresAt`. The cloud does NOT cause key drops (spec §4).
 */
export interface ActiveToxicCloud {
  id: string; // unique id (e.g. `cloud_${timestamp}`)
  casterId: string;
  tileIds: string[]; // tiles covered by the cloud
  damagePerTick: number; // Plan 4a default: 5
  expiresAt: number; // state.round at which the cloud is removed (cloud active while round <= expiresAt)
  createdRound?: number;
  createdTurnIndex?: number;
}

/**
 * Toxic Cloud DoT state — lives in GameState.activeClouds (added by Task 7.1 Toxic Cloud).
 * Placeholder reference here: the per-player field `toxicCloudAffected` only tracks whether a
 * tick-check should fire for the current player. The cloud state itself (caster, tile, turns
 * remaining, damagePerTick) lives on GameState, NOT on Player.
 */

/**
 * Active Cursed Relic lockout. One entry per cursed victim.
 * Decrements after the victim completes their own turn; death clears the entry.
 */
export interface ActiveCursedRelic {
  victimId: string;
  casterId: string;
  turnsRemaining: number; // starts at 2, decrements once per completed victim turn
  appliedTurnKey?: string; // skips same-turn decrement when applied during the victim's active turn
}

/**
 * Pending Orbital Cannon charge — Plan 4b. Single slot (cannon is global; only one charge
 * in flight at a time). Discharges at the START of the FIRST player's turn whose round
 * satisfies state.round >= fireOnRound. Cleared after discharge.
 */
export interface PendingOrbitalCannon {
  targetId: string;
  casterId: string;
  fireOnRound: number; // state.round + 1 at cast time
}

/**
 * Mystery Box trap entry — Plan 4b. Players in this list skip their next turn AND are
 * immune to all damage until expiresOnRound (spec §4 #10 Dark outcome).
 */
export interface MysteryBoxTrap {
  playerId: string;
  expiresOnRound: number; // state.round + 1 at cast time
}

/**
 * Pending Mystery Box "choose 3 minigames" choice — Plan 4b (gold outcome). Set when
 * Mystery Box rolls gold; cleared by handleMysteryBoxChoice. Only one pending choice at
 * a time game-wide (spec implicit; coordinator decision: a second gold cast while one is
 * pending fails with success:false to avoid choice ambiguity).
 */
export interface PendingMysteryBoxChoice {
  playerId: string;
  minigameOptions: [string, string, string]; // exactly 3 distinct minigame ids
}

/**
 * Pending Soul Duel — Plan 4b. Set when Soul Duel item is cast; cleared by resolveSoulDuel.
 * Only one Soul Duel at a time (subsequent cast fails with success:false).
 */
export interface PendingSoulDuel {
  challengerId: string;
  defenderId: string;
  minigameId: string; // chosen retro-minigame from SOUL_DUEL_MINIGAME_POOL
}

export type SoulAnchorMode = 'keys' | 'hp';

export interface PendingSoulAnchorClaim {
  playerId: string;
  nodeId: string;
  deadlineAtMs: number | null;
  resumeAfterPrompt?: 'rolling' | 'item_phase';
}

export type BoardEncounterKind = 'treasure_chest' | 'weapons_cache';

export interface PendingBoardEncounter {
  playerId: string;
  nodeId: string;
  kind: BoardEncounterKind;
  remainingSteps: number;
  diceResult: number;
  deadlineAtMs?: number | null;
  itemOptions?: string[];
  resumeAfterPrompt?: 'rolling' | 'item_phase';
}

export type ForcedRelocationReason = 'swap' | 'teleport' | 'admin_teleport' | 'dash';

export type ForcedRelocationSource =
  | 'dimension_rift'
  | 'mystery_box_blue'
  | 'admin_god_teleport'
  | 'lightning_dash';

export interface ForcedRelocationIntent {
  playerId: string;
  actorId: string;
  fromNodeId: string;
  toNodeId: string;
  reason: ForcedRelocationReason;
  source: ForcedRelocationSource;
}

export type RelocationPromptKind = BoardEncounterKind | 'soul_anchor';

export interface PendingRelocationPrompt {
  id: string;
  playerId: string;
  nodeId: string;
  kind: RelocationPromptKind;
  sourceRelocationId: string;
  source: ForcedRelocationSource;
  createdRound: number;
  createdCurrentPlayerIndex: number;
  resumeAfterPrompt: 'item_phase' | 'rolling';
  itemOptions?: string[];
}

export interface GamePauseState {
  pausedByPlayerId: string;
  pausedAtMs: number;
}

/**
 * Legacy Phoenix Potion buff metadata. Runtime Phoenix overheal is now represented by player.hp
 * above 30 and is consumed by damage, not by timed status decay.
 */
export interface PhoenixBuff {
  hpBonus: number;
  expiresAt: number;
}

/**
 * Stone Skin shield state. `active=true` means the next incoming hostile non-self effect against
 * this player is blocked and Stone Skin is consumed. Blocked effects include damage, key/item
 * theft, freeze, curse, hostile relocation, hostile duel triggers, and Shadow Stalker attacks.
 * Self-cast and self-inflicted effects do not consume it.
 */
export interface StoneSkinState {
  active: boolean;
}

/**
 * Per-player status effects. All fields optional/undefined when no effect is active.
 * `stunnedUntil` / `frozenUntil` / `toxicCloud.expiresAt` are ROUND numbers (state.round),
 * inclusive — e.g. `frozenUntil = 5` means the effect is active during round 5 and expires at the
 * START of round 6's player turn.
 */
export interface StatusEffects {
  stunnedUntil: number | null; // round number; null = not stunned
  frozenUntil: number | null; // round number; null = not frozen
  toxicCloudAffected: { expiresAt: number; damagePerTick: number } | null; // non-null when caster's
  // toxic cloud covers this player's
  // tile (set/cleared by Task 7.1)
  phoenixBuff: PhoenixBuff | null; // legacy-only; runtime Phoenix overheal lives in player.hp
  stoneSkin: StoneSkinState; // always present; stoneSkin.active flips true/false
}

/**
 * Game settings configurable by the host.
 * Spec §13: dice range, rounds, trophy cost, starting keys.
 * Only fields relevant to Plan 3a turn engine are included.
 */
export interface GameSettings {
  maxRounds: number; // Default 15, range 10-30 step 5 or 0 for unlimited
  targetTrophies?: number; // Default 3, range 1-10. Trophy-target end condition.
  turnTimer: number; // Seconds, default 30. 0 = unlimited
  startingKeys: number; // Default 35, range 0-150
  trophyCost: number; // Default 40, range 0-150
  minDiceRoll: number; // Default 1, range 0-32
  maxDiceRoll: number; // Default 20, range (minDiceRoll+1)-32
  playerCount: number; // Default 4, range 2-8
  weaponsCacheRespawn: number; // Default 3, range 1-8
  boardModifiers: BoardModifiers; // Plan 3b: pinata, doubleTime, oneHitKill
  betweenRoundMinigamesEnabled?: boolean; // Default true. False skips automatic board minigames between rounds.
  // Plan 3b+: rulesets, gobletToWin, enabledMinigames, enabledItems
}

export function isBetweenRoundMinigamesEnabled(settings: GameSettings): boolean {
  return settings.betweenRoundMinigamesEnabled !== false;
}

/**
 * Player state. Only fields needed by the turn engine.
 * Spec §3.1: Player interface.
 */
export interface Player {
  id: string;
  name: string;
  color: number;
  customization: CharacterCustomization; // 5,760 combinations (10×8×8×9). Server seeds on player creation.
  nodeId: string; // Current board position (BoardNode.id)
  hp: number; // Max 30, start 30
  keys: number; // Start from settings.startingKeys
  trophies: number; // Start 0
  items: string[]; // Item IDs — empty for Plan 3a (item system is Plan 4)
  isBot: boolean;
  isLocal: boolean;
  isReady?: boolean;
  connectionId: string | null;
  soulAnchorClaims: string[]; // Node IDs of soul anchors this player has claimed
  isDead: boolean; // True when HP <= 0 (waiting for respawn)
  besideGraveyardId: string | null; // Non-null when respawned "beside" a graveyard (immune to passing effects)
  statusEffects: StatusEffects; // Active status effects for this player (Plan 4a)
  cursedRelicTurnsRemaining: number; // 0 = not cursed; >0 = Cursed Relic item/key-pickup lockout. Mirrors state.activeCursedRelics.
}

export interface LastMove {
  previousNodeId: string | null;
}

export interface GodModeImmunity {
  noDamage: boolean;
  noKeyLoss: boolean;
}

export interface DroppedKeyPile {
  ownerPlayerId: string | null;
  amount: number;
}

/**
 * Top-level game state. Only fields needed for the turn engine.
 * Spec §3.1: GameState interface.
 */
export interface GameState {
  phase: Phase;
  round: number; // Current round (1-indexed, starts at 1)
  maxRounds: number; // From settings, copied for easy access
  currentPlayerIndex: number; // Index into turnOrder
  turnOrder: string[]; // Player IDs in turn order
  players: Map<string, Player>;
  boardNodeIds: string[]; // All node IDs on the board (for validation)
  treasureChestNodeId: string;
  settings: GameSettings;
  eventLog: GameEvent[]; // Append-only, defined in Task 1.2
  rngSeed: number; // Seed for deterministic RNG
  rngState: number; // Current RNG counter (mutated by dice rolls)
  weaponsCacheNodeId: string | null; // Current weapons cache location (null if respawning)
  weaponsCacheRespawnIn: number; // Turns until weapons cache respawns (0 = active)
  stats: Map<string, PlayerStats>; // Per-player stats for Pummel Awards
  boardModifiers: BoardModifiers; // Active board modifier flags (copied from settings)
  droppedKeys: Map<string, DroppedKeyPile[] | number>; // nodeId -> owned key piles; numeric entries are legacy ownerless piles
  lastMoveByPlayer: Map<string, LastMove>; // playerId -> prior node from the last normal graph step
  activeClouds: ActiveToxicCloud[]; // Active Toxic Cloud effects (added by item_toxic_cloud; ticks each turn start)
  npcs: Npc[]; // Active NPCs on the board (Plan 5 — Shadow Stalker; future: more kinds via Npc union)
  // (NPC types — see Npc union in npc/types.ts)
  activeCursedRelics: ActiveCursedRelic[]; // Plan 4b
  pendingOrbitalCannon: PendingOrbitalCannon | null; // Plan 4b
  mysteryBoxTraps: MysteryBoxTrap[]; // Plan 4b
  pendingMysteryBoxChoice: PendingMysteryBoxChoice | null; // Plan 4b
  pendingSoulDuel: PendingSoulDuel | null; // Plan 4b
  soulAnchorClaimModes?: Map<string, SoulAnchorMode>;
  pendingSoulAnchorClaim?: PendingSoulAnchorClaim | null;
  pendingBoardEncounter?: PendingBoardEncounter | null;
  pendingRelocationPrompts?: PendingRelocationPrompt[];
  pendingForkChoice?: PendingForkChoice | null;
  endGame?: EndGameSnapshot | null;
  awardsApplied?: boolean;
  /** Absolute server deadline for the active player's pre-roll item window. Null/undefined = no timer. */
  turnActionDeadlineAtMs?: number | null;
  /** Absolute server deadline for the active player's fork choice. Null/undefined = no timer. */
  forkChoiceDeadlineAtMs?: number | null;
  /** Server-authoritative pause marker. Null/undefined = not paused. */
  pause?: GamePauseState | null;
  /** Dev-only god-mode testing overrides, keyed by playerId. Not serialized in normal state. */
  godModeImmunity?: Map<string, GodModeImmunity>;
}

/**
 * Default board modifiers.
 */
export const DEFAULT_BOARD_MODIFIERS: BoardModifiers = {
  pinata: false,
  doubleTime: false,
  oneHitKill: false,
};

/**
 * Default game settings matching spec §13 "Create Game — Default" column.
 */
export const DEFAULT_GAME_SETTINGS: GameSettings = {
  maxRounds: 15,
  targetTrophies: 3,
  turnTimer: 30,
  startingKeys: 35,
  trophyCost: 40,
  minDiceRoll: 1,
  maxDiceRoll: 20,
  playerCount: 8,
  weaponsCacheRespawn: 3,
  boardModifiers: DEFAULT_BOARD_MODIFIERS,
  betweenRoundMinigamesEnabled: true,
};

/**
 * Callback signature for step-by-step movement animation hooks.
 * The server/shared layer calls this for each step; the client layer subscribes (in a later plan).
 * Parameters:
 *   playerId — the moving player
 *   fromNodeId — node the player is leaving
 *   toNodeId — node the player is entering
 *   stepIndex — 0-based index within the current move
 *   totalSteps — total steps in this move (dice roll result)
 */
export type StepHook = (
  playerId: string,
  fromNodeId: string,
  toNodeId: string,
  stepIndex: number,
  totalSteps: number,
) => void;

export type MovementNodeEntryHook = (
  playerId: string,
  fromNodeId: string,
  toNodeId: string,
  stepIndex: number,
  totalSteps: number,
) => 'continue' | 'stop' | void;

/**
 * Callback signature for fork choice resolution.
 * Called when a player reaches a fork (node with multiple outgoing connections).
 * Must return the chosen target node ID (one of the fork options).
 * Async to support player input (UI prompt) or bot AI decision.
 */
export type ForkChoiceResolver = (
  playerId: string,
  currentNodeId: string,
  options: string[], // Available target node IDs
  context?: ForkChoiceContext,
) => Promise<string>;

export interface ForkChoiceContext {
  diceResult: number;
  stepsUsed: number;
  remainingSteps: number;
}

export interface PendingForkChoice {
  id: string;
  playerId: string;
  atNodeId: string;
  options: string[];
  diceResult: number;
  stepsUsed: number;
  remainingSteps: number;
  deadlineAtMs: number | null;
}

export type EndGameReason = 'round_limit' | 'trophy_target';

export interface RankingEntry {
  playerId: string;
  rank: number;
  trophies: number;
  keys: number;
}

export interface AwardTieBreak {
  tiedPlayerIds: string[];
  resolvedBy: 'pre_award_ranking';
}

export type AwardCategoryId =
  | 'most_keys_gained'
  | 'most_damage_dealt'
  | 'minigame_champion'
  | 'most_tiles_traveled'
  | 'most_items_used'
  | 'most_minigames_won';

export interface AwardSnapshot {
  awardId: string;
  categoryId: AwardCategoryId;
  categoryLabel: string;
  playerId: string;
  statValue: number;
  trophyDelta: number;
  tieBreak?: AwardTieBreak;
}

export interface EndGameSnapshot {
  id: string;
  reason: EndGameReason;
  triggeredAtRound: number;
  triggerPlayerId?: string;
  preAwardWinnerPlayerId: string | null;
  winnerPlayerId: string | null;
  preAwardRankings: RankingEntry[];
  rankings: RankingEntry[];
  awards: AwardSnapshot[];
  awardsApplied: boolean;
}

/**
 * Result of executing a player's movement.
 */
export interface MovementResult {
  finalNodeId: string; // Where the player ended up
  path: string[]; // Full path traversed (including pathing nodes)
  stepsUsed: number; // Actual steps consumed (excludes pathing nodes)
  forksEncountered: number; // Number of fork choices made during movement
  stoppedOnNodeId?: string;
  stoppedByDeath?: boolean;
}

/**
 * Base fields shared by all game events.
 * Every event has a type discriminant, a timestamp (monotonic counter, not wall-clock),
 * and a playerId (null for system events like phase_change).
 */
interface GameEventBase {
  timestamp: number; // Monotonic counter incremented per event
  playerId: string | null; // null for system-level events
}

/**
 * Emitted when a player's turn starts.
 */
export interface TurnStartEvent extends GameEventBase {
  type: 'turn_start';
  playerId: string; // Override: always non-null
  round: number;
}

/**
 * Emitted when a player's turn ends.
 */
export interface TurnEndEvent extends GameEventBase {
  type: 'turn_end';
  playerId: string; // Override: always non-null
  round: number;
}

/**
 * Emitted when the game phase changes.
 */
export interface PhaseChangeEvent extends GameEventBase {
  type: 'phase_change';
  fromPhase: Phase;
  toPhase: Phase;
}

/**
 * Emitted when a dice is rolled.
 */
export interface DiceRollEvent extends GameEventBase {
  type: 'dice_roll';
  playerId: string; // Override: always non-null
  result: number; // The dice roll value
}

/**
 * Emitted for each step during movement.
 */
export interface MovementStepEvent extends GameEventBase {
  type: 'movement_step';
  playerId: string; // Override: always non-null
  fromNodeId: string;
  toNodeId: string;
  stepIndex: number;
  totalSteps: number;
}

/**
 * Emitted when a player makes a fork choice.
 */
export interface ForkChoiceEvent extends GameEventBase {
  type: 'fork_choice';
  playerId: string; // Override: always non-null
  atNodeId: string; // The fork node
  options: string[]; // Available target node IDs
  chosenNodeId: string; // The chosen target
}

/**
 * Emitted when a player lands on a tile (final tile after movement).
 */
export interface LandingEvent extends GameEventBase {
  type: 'landing';
  playerId: string; // Override: always non-null
  nodeId: string; // The tile landed on
  tileType: string; // TileType of the tile (string to avoid circular import)
}

/**
 * Emitted when a transient board object is consumed from a tile.
 * Dropped keys use this for immediate visual cleanup; future floating board
 * objects can extend objectKind without tying visuals to economy events.
 */
export interface BoardObjectCollectedEvent extends GameEventBase {
  type: 'board_object_collected';
  playerId: string;
  nodeId: string;
  objectKind: 'dropped_keys';
  quantity: number;
  remainingDroppedKeys: DroppedKeyPile[];
}

/**
 * Emitted when a player's keys or trophies change.
 */
export interface EconomyChangeEvent extends GameEventBase {
  type: 'economy_change';
  playerId: string;
  resource: 'keys' | 'trophies';
  oldValue: number;
  newValue: number;
  reason: string; // e.g. 'gold_tile', 'hazard_drop', 'trophy_purchase', 'death_drop', 'soul_anchor_steal'
}

/**
 * Emitted when a player takes damage.
 */
export interface DamageEvent extends GameEventBase {
  type: 'damage';
  playerId: string; // The player taking damage
  sourcePlayerId: string | null; // null for tile/environment damage
  amount: number;
  source: string; // e.g. 'hazard_tile', 'item_thunder_fist', 'soul_anchor'
  newHp: number; // HP after damage applied
}

/**
 * Emitted when a player's HP changes (heal or damage).
 */
export interface HealEvent extends GameEventBase {
  type: 'heal';
  playerId: string;
  amount: number;
  source: string; // e.g. 'heal_tile', 'item_phoenix_potion'
  newHp: number;
}

/**
 * Emitted when a player dies (HP reaches 0).
 */
export interface DeathEvent extends GameEventBase {
  type: 'death';
  playerId: string;
  killerPlayerId: string | null; // null for environment kills
  cause: string; // e.g. 'hazard_tile', 'item_thunder_fist'
  keysDropped: number;
}

/**
 * Emitted when a player respawns after death.
 */
export interface RespawnEvent extends GameEventBase {
  type: 'respawn';
  playerId: string;
  graveyardNodeId: string; // The graveyard node chosen for respawn
}

/**
 * Emitted when a player claims a soul anchor tile.
 */
export interface SoulAnchorClaimEvent extends GameEventBase {
  type: 'soul_anchor_claim';
  playerId: string;
  nodeId: string; // The soul anchor tile
  connectedNodeIds: string[]; // Other anchors claimed together
  mode?: SoulAnchorMode;
}

/**
 * Emitted when a soul anchor triggers its effect on a non-owner landing.
 */
export interface SoulAnchorTriggerEvent extends GameEventBase {
  type: 'soul_anchor_trigger';
  playerId: string; // The player who landed (victim)
  ownerPlayerId: string; // The anchor owner
  nodeId: string;
  effectType: 'keys' | 'hp';
  amount: number; // Keys stolen or HP drained
}

/**
 * Emitted when a board modifier triggers its effect.
 */
export interface ModifierTriggeredEvent extends GameEventBase {
  type: 'modifier_triggered';
  modifier: string; // e.g. 'pinata', 'one_hit_kill', 'double_time'
  detail: string; // Human-readable description of the trigger
}

/**
 * Emitted when a Pummel Award is granted at end of game.
 */
export interface AwardGrantedEvent extends GameEventBase {
  type: 'award_granted';
  playerId: string; // The recipient
  category: string; // Backward-compatible category alias; usually matches categoryId.
  value: number; // The stat value that won
  trophiesGranted: number; // Always 1 per spec
  awardId?: string;
  categoryId?: AwardCategoryId;
  categoryLabel?: string;
  statValue?: number;
  trophyDelta?: number;
  tieBreak?: AwardTieBreak;
}

export type PlayerReceivedItemSource =
  | 'item_tile'
  | 'weapons_cache'
  | 'mystery_box'
  | 'item_theft'
  | 'admin_god_add_item'
  | 'minigame_reward';

/**
 * Emitted when gameplay grants an item to a player.
 */
export interface PlayerReceivedItemEvent extends GameEventBase {
  type: 'player_received_item';
  playerId: string;
  itemId: string;
  source: PlayerReceivedItemSource;
}

/**
 * Emitted when a player casts an item. One event per cast, BEFORE any hit/miss/block event.
 */
export interface ItemCastEvent extends GameEventBase {
  type: 'item_cast';
  playerId: string; // Caster (GameEventBase.playerId override — always non-null)
  casterId: string; // Duplicate of playerId for discoverability
  itemId: string;
  targetId?: string; // Populated when targetType='player'
  targetNodeId?: string; // Populated when targetType='tile' or graph-directed skillshots
  /** Populated when targetType='direction'. `dy` is board-space z for wire compatibility. */
  direction?: { dx: number; dy: number };
}

/**
 * Emitted when a damaging item hits a target successfully.
 */
export interface ItemHitEvent extends GameEventBase {
  type: 'item_hit';
  playerId: string; // Caster
  casterId: string;
  itemId: string;
  targetId: string;
  damageDealt: number;
  targetNodeId?: string;
  nodeId?: string;
  path?: string[];
  toNodeId?: string;
  /** `dy` is board-space z for wire compatibility. */
  direction?: { dx: number; dy: number };
}

/**
 * Emitted when a skillshot item misses its target.
 */
export interface ItemMissedEvent extends GameEventBase {
  type: 'item_missed';
  playerId: string; // Caster
  casterId: string;
  itemId: string;
  targetId?: string;
  targetNodeId?: string;
  path?: string[];
  toNodeId?: string;
  /** `dy` is board-space z for wire compatibility. */
  direction?: { dx: number; dy: number };
}

/**
 * Emitted when a target's Stone Skin blocks a hostile effect.
 * `itemId` is the canonical blocking source id: item ids such as `cursed_relic`, or non-item
 * sources such as `hazard_tile`, `soul_anchor`, and `shadow_stalker`.
 */
export interface ItemBlockedStoneSkinEvent extends GameEventBase {
  type: 'item_blocked_stoneskin';
  playerId: string; // Actor/source that attempted the hostile effect, or blocker for environment sources
  casterId: string; // Actor/source that attempted the hostile effect
  itemId: string;
  blockerId: string; // The player whose Stone Skin blocked
}

/**
 * Emitted when Dimension Rift swaps positions between caster and target.
 */
export interface ItemSwapPositionsEvent extends GameEventBase {
  type: 'item_swap_positions';
  playerId: string; // Caster
  casterId: string;
  targetId: string;
  casterOldNodeId: string;
  targetOldNodeId: string;
}

export interface ForcedRelocationEvent extends GameEventBase {
  type: 'forced_relocation';
  playerId: string;
  relocationId: string;
  sequence: number;
  actorId: string;
  source: ForcedRelocationSource;
  reason: ForcedRelocationReason;
  relocations: ForcedRelocationIntent[];
}

/**
 * Emitted when a status effect is applied.
 */
export interface StatusEffectAppliedEvent extends GameEventBase {
  type: 'status_effect_applied';
  playerId: string; // The affected player
  effectType: 'stunned' | 'frozen' | 'toxicCloud' | 'phoenixBuff' | 'stoneSkin';
  duration: number; // Turns; 0 for stoneSkin (no duration)
  source: string; // e.g. 'item_frost_blaster', 'item_stone_skin'
}

/**
 * Emitted when a status effect expires (or is consumed, for Stone Skin).
 */
export interface StatusEffectExpiredEvent extends GameEventBase {
  type: 'status_effect_expired';
  playerId: string;
  effectType: 'stunned' | 'frozen' | 'toxicCloud' | 'phoenixBuff' | 'stoneSkin';
}

/**
 * Emitted when a player with active Stone Skin attempts to use another item; UI consumes this in a
 * later plan to prompt "Using this item will discard Stone Skin. Continue?". Plan 4a logic layer
 * emits this and waits for a confirm callback/flag (see Task 9.4 for the full flow).
 */
export interface StoneSkinDiscardPromptEvent extends GameEventBase {
  type: 'stone_skin_discard_prompt';
  playerId: string; // The Stone Skin holder
  pendingItemId: string; // The item the player tried to use
}

/**
 * Emitted when an NPC is spawned. Plan 5 emits this for shadow_stalker spawns
 * (called from spawnShadowStalker — see npc/registry.ts).
 */
export interface NpcSpawnedEvent extends GameEventBase {
  type: 'npc_spawned';
  playerId: string | null; // null for system-spawned; equals summonerId for stalker
  npcId: string;
  npcKind: 'shadow_stalker';
  nodeId: string; // spawn node
  summonerId: string; // for stalker; equals playerId
}

/**
 * Emitted for each step an NPC takes during its tick.
 */
export interface NpcMovedEvent extends GameEventBase {
  type: 'npc_moved';
  playerId: null;
  npcId: string;
  fromNodeId: string;
  toNodeId: string;
  stepIndex: number; // 0-based within this tick
  totalSteps: number; // max steps for this tick (4 for stalker)
}

/**
 * Emitted when an NPC kills a player. Emitted BEFORE the standard 'damage' + 'death' events
 * from applyDamage (which fires the actual death pipeline) so replay/UI consumers see the
 * NPC causation ahead of the generic damage/death events. Ordering updated in polish-pass P5-1.
 */
export interface NpcKilledPlayerEvent extends GameEventBase {
  type: 'npc_killed_player';
  playerId: string; // victim
  npcId: string;
  npcKind: 'shadow_stalker';
  atNodeId: string;
}

/**
 * Emitted when an NPC is despawned (e.g. single-stalker replacement per spec §4 rule 4).
 */
export interface NpcDespawnedEvent extends GameEventBase {
  type: 'npc_despawned';
  playerId: null;
  npcId: string;
  reason: 'replaced_by_new_stalker' | 'game_over' | 'killed_player';
}

/**
 * Plan 4b — Gravity Well events.
 */
export interface ItemPullEvent extends GameEventBase {
  type: 'item_pull';
  playerId: string; // Caster
  casterId: string;
  itemId: 'gravity_well';
  victimId: string;
  pulledFromNodeId: string;
  stoleItem: boolean;
}

export interface ItemStealKeysEvent extends GameEventBase {
  type: 'item_steal_keys';
  playerId: string; // Caster (recipient)
  casterId: string;
  itemId: string; // 'gravity_well' for now; future steal-items may reuse
  victimId: string;
  amount: number;
}

export interface ItemStealItemEvent extends GameEventBase {
  type: 'item_steal_item';
  playerId: string; // Caster (recipient)
  casterId: string;
  itemId: 'gravity_well';
  victimId: string;
  stolenItemId: string;
}

/**
 * Plan 4b — Lightning Dash events.
 */
export interface ItemDashEvent extends GameEventBase {
  type: 'item_dash';
  playerId: string; // Caster
  casterId: string;
  itemId: 'lightning_dash';
  path: string[]; // node ids walked, including final tile
  fromNodeId?: string;
  toNodeId?: string;
}

export interface ItemDashHitEvent extends GameEventBase {
  type: 'item_dash_hit';
  playerId: string; // Caster
  casterId: string;
  itemId: 'lightning_dash';
  victimId: string;
  damageDealt: number;
  nodeId?: string;
  pathIndex?: number;
}

/**
 * Plan 4b — Orbital Cannon events.
 */
export interface ItemChargeEvent extends GameEventBase {
  type: 'item_charge';
  playerId: string; // Caster
  casterId: string;
  itemId: 'orbital_cannon';
  targetId: string;
  fireOnRound: number;
}

export interface ItemBeamFiredEvent extends GameEventBase {
  type: 'item_beam_fired';
  playerId: string; // Caster (recorded for replay)
  casterId: string;
  itemId: 'orbital_cannon';
  targetId: string;
  killed: boolean; // false if blocked by Stone Skin or target was missing/dead
}

/**
 * Plan 4b — Cursed Relic DoT events.
 */
export interface ItemDotAppliedEvent extends GameEventBase {
  type: 'item_dot_applied';
  playerId: string; // Caster
  casterId: string;
  itemId: 'cursed_relic';
  victimId: string;
  turnsRemaining: number; // 2 at apply
}

export interface ItemDotTickEvent extends GameEventBase {
  type: 'item_dot_tick';
  playerId: string; // Victim
  victimId: string;
  casterId: string;
  itemId: 'cursed_relic';
  damageDealt: number;
  keysDropped: number;
  turnsRemaining: number; // after this tick
}

export interface ItemDotExpiredEvent extends GameEventBase {
  type: 'item_dot_expired';
  playerId: string; // Victim
  victimId: string;
  itemId: 'cursed_relic';
}

/**
 * Plan 4b — Mystery Box events.
 */
export interface MysteryBoxOutcomeEvent extends GameEventBase {
  type: 'mystery_box_outcome';
  playerId: string; // Caster
  casterId: string;
  itemId: 'mystery_box';
  outcome: 'blue' | 'gold' | 'dark' | 'gold_fallback_item';
}

export interface MysteryBoxTeleportEvent extends GameEventBase {
  type: 'mystery_box_teleport';
  playerId: string; // Caster
  casterId: string;
  fromNodeId: string;
  toNodeId: string;
}

export interface MysteryBoxChoiceRequiredEvent extends GameEventBase {
  type: 'mystery_box_choice_required';
  playerId: string; // Caster
  casterId: string;
  minigameOptions: [string, string, string];
}

export interface MysteryBoxChoiceResolvedEvent extends GameEventBase {
  type: 'mystery_box_choice_resolved';
  playerId: string; // Caster
  casterId: string;
  chosenMinigameId: string;
}

export interface MysteryBoxTrappedEvent extends GameEventBase {
  type: 'mystery_box_trapped';
  playerId: string; // Caster (self-trap)
  casterId: string;
  expiresOnRound: number;
}

/**
 * Plan 4b — Shadow Stalker (item) summon event. Plan 5 emits npc_spawned separately;
 * this event is the item-side breadcrumb tying the summon to its cast.
 */
export interface NpcSummonedEvent extends GameEventBase {
  type: 'npc_summoned';
  playerId: string; // Summoner
  casterId: string;
  itemId: 'shadow_stalker';
  npcId: string;
}

/**
 * Plan 4b — Soul Duel events.
 */
export interface SoulDuelTriggeredEvent extends GameEventBase {
  type: 'soul_duel_triggered';
  playerId: string; // Challenger
  challengerId: string;
  defenderId: string;
  minigameId: string;
}

export interface SoulDuelResolvedEvent extends GameEventBase {
  type: 'soul_duel_resolved';
  playerId: string; // Winner
  winnerId: string;
  loserId: string;
  trophiesGranted: number; // always 1
}

/**
 * Discriminated union of all game events for Plan 3a + Plan 3b + Plan 4a + Plan 5.
 */
export type GameEvent =
  | TurnStartEvent
  | TurnEndEvent
  | PhaseChangeEvent
  | DiceRollEvent
  | MovementStepEvent
  | ForkChoiceEvent
  | LandingEvent
  | BoardObjectCollectedEvent
  | EconomyChangeEvent
  | DamageEvent
  | HealEvent
  | DeathEvent
  | RespawnEvent
  | SoulAnchorClaimEvent
  | SoulAnchorTriggerEvent
  | ModifierTriggeredEvent
  | AwardGrantedEvent
  | PlayerReceivedItemEvent
  | ItemCastEvent
  | ItemHitEvent
  | ItemMissedEvent
  | ItemBlockedStoneSkinEvent
  | ItemSwapPositionsEvent
  | ForcedRelocationEvent
  | StatusEffectAppliedEvent
  | StatusEffectExpiredEvent
  | StoneSkinDiscardPromptEvent
  | NpcSpawnedEvent
  | NpcMovedEvent
  | NpcKilledPlayerEvent
  | NpcDespawnedEvent
  // Plan 4b
  | ItemPullEvent
  | ItemStealKeysEvent
  | ItemStealItemEvent
  | ItemDashEvent
  | ItemDashHitEvent
  | ItemChargeEvent
  | ItemBeamFiredEvent
  | ItemDotAppliedEvent
  | ItemDotTickEvent
  | ItemDotExpiredEvent
  | MysteryBoxOutcomeEvent
  | MysteryBoxTeleportEvent
  | MysteryBoxChoiceRequiredEvent
  | MysteryBoxChoiceResolvedEvent
  | MysteryBoxTrappedEvent
  | NpcSummonedEvent
  | SoulDuelTriggeredEvent
  | SoulDuelResolvedEvent;

/**
 * All possible GameEvent type discriminants.
 */
export type GameEventType = GameEvent['type'];
